// ══════════════════════════════════════════════════════
// Portfolio Analyzer — Multi-Strategy Correlation & Risk Analysis
// ══════════════════════════════════════════════════════

import { useState, useMemo, useEffect } from "react";
import { theme, fonts } from "../theme";
import { Card, CardHeader, Button, Pill, formatPnl } from "../components/ui";
import { useApi } from "../hooks/useApi";
import { getApi } from "./strategy-lab/constants";
import { toast } from "../components/shared";
import ScenarioPlanner from "./ScenarioPlanner";

/* ── Mock Data Generator (replaced by real backend later) ─────────────── */
function generateMockTrades(strategyName, seed, months = 24) {
  const trades = [];
  const rng = (n) => { const x = Math.sin(seed * 9999 + n) * 10000; return x - Math.floor(x); };
  const startDate = new Date(2024, 3, 1);
  let equity = 50000;
  let peakEquity = 50000;
  let maxDD = 0;

  // Each strategy has a characteristic bias
  const characteristics = {
    win_rate: 0.45 + rng(1) * 0.25,
    avg_win: 150 + rng(2) * 300,
    avg_loss: -(100 + rng(3) * 200),
    trades_per_day: 0.5 + rng(4) * 2,
  };

  let currentDate = new Date(startDate);
  const endDate = new Date(startDate);
  endDate.setMonth(endDate.getMonth() + months);
  let tradeIdx = 0;

  while (currentDate < endDate) {
    if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
      const tradesToday = rng(tradeIdx + 100) < characteristics.trades_per_day ? Math.ceil(rng(tradeIdx+200)*3) : 0;
      for (let i = 0; i < tradesToday; i++) {
        const isWin = rng(tradeIdx + 1000) < characteristics.win_rate;
        const pnl = isWin
          ? characteristics.avg_win * (0.5 + rng(tradeIdx+2000))
          : characteristics.avg_loss * (0.5 + rng(tradeIdx+3000));
        const entryHour = 9 + Math.floor(rng(tradeIdx+4000) * 7);
        const durationMin = 30 + Math.floor(rng(tradeIdx+5000) * 180);
        const entryTime = new Date(currentDate);
        entryTime.setHours(entryHour, Math.floor(rng(tradeIdx+6000)*60), 0);
        const exitTime = new Date(entryTime.getTime() + durationMin * 60 * 1000);
        equity += pnl;
        peakEquity = Math.max(peakEquity, equity);
        const dd = ((peakEquity - equity) / peakEquity) * 100;
        maxDD = Math.max(maxDD, dd);
        trades.push({
          strategy: strategyName,
          entry: entryTime,
          exit: exitTime,
          pnl: Math.round(pnl * 100) / 100,
          equity: Math.round(equity * 100) / 100,
          margin: 1000 + Math.floor(rng(tradeIdx+7000) * 2000),
        });
        tradeIdx++;
      }
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return { trades, finalEquity: equity, maxDD };
}

/* ── Compute Daily P&L Series from Trades ─────────────── */
function computeDailyPnl(trades) {
  const daily = {};
  trades.forEach(t => {
    const dayKey = t.exit.toISOString().split("T")[0];
    daily[dayKey] = (daily[dayKey] || 0) + t.pnl;
  });
  return Object.entries(daily).sort(([a],[b]) => a.localeCompare(b));
}

/* ── Pearson Correlation ─────────────── */
function pearson(x, y) {
  const n = Math.min(x.length, y.length);
  if (n < 2) return 0;
  const meanX = x.slice(0,n).reduce((a,b)=>a+b,0)/n;
  const meanY = y.slice(0,n).reduce((a,b)=>a+b,0)/n;
  let num=0, denX=0, denY=0;
  for (let i=0; i<n; i++) {
    const dx = x[i]-meanX; const dy = y[i]-meanY;
    num += dx*dy; denX += dx*dx; denY += dy*dy;
  }
  const den = Math.sqrt(denX*denY);
  return den === 0 ? 0 : num/den;
}

/* ── Main Component ─────────────── */
export default function Portfolio() {
  const { data: stratFiles } = useApi("/strategy-files", 10000);
  const [selectedStrategies, setSelectedStrategies] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accountSize, setAccountSize] = useState(100000);
  const [useMockData, setUseMockData] = useState(true); // Toggle for real vs mock data
  const [view, setView] = useState("scenario");  // R7s-2: scenario | analyzer

  // Get strategy list from backend
  const allStrategies = useMemo(() => {
    if (!stratFiles) return [];
    const files = Array.isArray(stratFiles) ? stratFiles : (stratFiles.files || []);
    return files.map(f => {
      if (typeof f === "string") return { filename: f, name: f, status: "test" };
      return f;
    });
  }, [stratFiles]);

  const toggleStrategy = (filename) => {
    setSelectedStrategies(prev =>
      prev.includes(filename) ? prev.filter(f => f !== filename) : [...prev, filename]
    );
  };

  const runAnalysis = async () => {
    if (selectedStrategies.length < 2) {
      toast("Wähle mindestens 2 Strategien aus", "error");
      return;
    }
    setLoading(true);

    // ── Backend-Aufruf (für echte Daten):
    // const result = await getApi(`/portfolio/analyze?strategies=${selectedStrategies.join(",")}`);

    // ── Mock-Daten-Generierung für jetzt:
    await new Promise(r => setTimeout(r, 800)); // Simulate loading

    const strategiesData = selectedStrategies.map((filename, idx) => {
      const { trades, finalEquity, maxDD } = generateMockTrades(filename, idx + 1);
      const totalPnl = finalEquity - 50000;
      const wins = trades.filter(t => t.pnl > 0);
      const winRate = trades.length > 0 ? wins.length / trades.length : 0;
      const dailyPnl = computeDailyPnl(trades);
      return { filename, trades, totalPnl, maxDD, winRate, dailyPnl };
    });

    // Correlation Matrix
    const allDays = [...new Set(strategiesData.flatMap(s => s.dailyPnl.map(([d]) => d)))].sort();
    const pnlSeries = strategiesData.map(s => {
      const dayMap = Object.fromEntries(s.dailyPnl);
      return allDays.map(d => dayMap[d] || 0);
    });
    const corrMatrix = strategiesData.map((_, i) =>
      strategiesData.map((_, j) => i === j ? 1 : pearson(pnlSeries[i], pnlSeries[j]))
    );

    // Combined Equity Curve
    const combinedDaily = allDays.map((d, di) => {
      const daySum = pnlSeries.reduce((sum, series) => sum + (series[di] || 0), 0);
      return { date: d, dailyPnl: daySum };
    });
    let cumul = 0; let peak = 0; let portfolioMaxDD = 0;
    const combinedEquity = combinedDaily.map(d => {
      cumul += d.dailyPnl;
      peak = Math.max(peak, cumul);
      const dd = peak > 0 ? ((peak - cumul) / (50000 * selectedStrategies.length + peak)) * 100 : 0;
      portfolioMaxDD = Math.max(portfolioMaxDD, dd);
      return { ...d, cumul, dd };
    });

    // Concurrent Trades — sample per hour
    const allTrades = strategiesData.flatMap(s => s.trades);
    const concurrentSamples = [];
    const sampleHours = 24 * 30; // Last 30 days sample
    const now = new Date(Math.max(...allTrades.map(t => t.exit.getTime())));
    for (let h = 0; h < sampleHours; h++) {
      const time = new Date(now.getTime() - h * 3600 * 1000);
      const active = allTrades.filter(t => t.entry <= time && t.exit >= time).length;
      concurrentSamples.unshift({ time, count: active });
    }
    const maxConcurrent = Math.max(...concurrentSamples.map(s => s.count), 0);
    const avgConcurrent = concurrentSamples.reduce((s,x) => s+x.count, 0) / concurrentSamples.length;

    // Max Margin needed
    const maxMargin = Math.max(...concurrentSamples.map(s => {
      const activeTradesAt = allTrades.filter(t => t.entry <= s.time && t.exit >= s.time);
      return activeTradesAt.reduce((sum, t) => sum + t.margin, 0);
    }), 0);

    // Sum of individual maxDDs (worst case)
    const sumIndividualDDs = strategiesData.reduce((s,x) => s + x.maxDD, 0);
    const ddReduction = sumIndividualDDs > 0 ? ((sumIndividualDDs - portfolioMaxDD) / sumIndividualDDs) * 100 : 0;

    setAnalysis({
      strategies: strategiesData,
      corrMatrix,
      combinedEquity,
      concurrentSamples,
      maxConcurrent,
      avgConcurrent,
      maxMargin,
      portfolioMaxDD,
      sumIndividualDDs,
      ddReduction,
      totalPortfolioPnl: strategiesData.reduce((s,x) => s + x.totalPnl, 0),
    });
    setLoading(false);
  };

  return <div style={{ padding:"20px 28px", maxWidth:1600, margin:"0 auto" }}>
    {/* Header */}
    <div style={{ marginBottom:14 }}>
      <h1 style={{ fontSize:24, fontWeight:800, color:theme.text, marginBottom:4 }}>Portfolio</h1>
      <p style={{ fontSize:13, color:theme.text3, maxWidth:800, lineHeight:1.6 }}>
        {view === "scenario"
          ? "Spiele Risk-Szenarien auf deinen Backtest-Daten durch. Mit Compounding, Charts und Monte-Carlo."
          : <>Analysiere mehrere Strategien gemeinsam. Prüfe Korrelationen, kombinierten Drawdown, Margin-Bedarf und parallele Trades — <strong>bevor</strong> du sie alle live schaltest.</>}
      </p>
    </div>

    {/* R7s-2: Tab Switcher */}
    <div style={{ display:"flex", gap:0, marginBottom:20, borderBottom:`1px solid ${theme.divider}` }}>
      <button onClick={() => setView("scenario")} style={{ padding:"10px 20px", fontSize:13, fontWeight:view==="scenario"?700:500, color:view==="scenario"?theme.blueText:theme.text3, background:"transparent", border:"none", borderBottom:view==="scenario"?`2px solid ${theme.blue}`:"2px solid transparent", cursor:"pointer", marginBottom:-1 }}>
        🎯 Scenario Planner
      </button>
      <button onClick={() => setView("analyzer")} style={{ padding:"10px 20px", fontSize:13, fontWeight:view==="analyzer"?700:500, color:view==="analyzer"?theme.blueText:theme.text3, background:"transparent", border:"none", borderBottom:view==="analyzer"?`2px solid ${theme.blue}`:"2px solid transparent", cursor:"pointer", marginBottom:-1 }}>
        📊 Multi-Strategy Analyzer
      </button>
    </div>

    {view === "scenario" && <ScenarioPlanner/>}

    {view === "analyzer" && <>
    {/* Mock Data Warning */}
    {useMockData && <Card style={{ marginBottom:16, padding:"12px 18px", background:theme.amberLight||"rgba(251,191,36,0.08)", border:`1px solid ${theme.amber||"#FBB424"}` }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ fontSize:12.5, color:theme.text2, lineHeight:1.5 }}>
          <strong>⚠️ Beta / Mock-Modus:</strong> Dieses Tool verwendet aktuell generierte Beispieldaten zur Demonstration.
          Die Backend-Integration mit echten Backtest-Ergebnissen folgt nach der Engine-Neuschreibung.
          Die Berechnungen (Korrelation, DD, Margin) sind bereits produktionsreif.
        </div>
        <Button onClick={() => setUseMockData(!useMockData)} style={{ fontSize:11 }}>
          {useMockData ? "Info ausblenden" : "Info einblenden"}
        </Button>
      </div>
    </Card>}

    <div style={{ display:"grid", gridTemplateColumns:"320px 1fr", gap:16, alignItems:"start" }}>
      {/* ── Sidebar: Strategy Selection ─────────── */}
      <Card style={{ position:"sticky", top:20 }}>
        <CardHeader title={`Strategien auswählen (${selectedStrategies.length})`} sub="Min. 2 für Analyse"/>
        <div style={{ padding:"8px 0", maxHeight:600, overflow:"auto" }}>
          {allStrategies.length === 0 && <div style={{ padding:20, textAlign:"center", color:theme.text3, fontSize:13 }}>
            Keine Strategien gefunden. Erstelle welche im Strategy Lab.
          </div>}
          {allStrategies.map(s => {
            const isSelected = selectedStrategies.includes(s.filename);
            const status = s.status || "test";
            const statusColor = status === "live" ? theme.greenText : status === "validated" ? theme.blueText : theme.text3;
            return <label key={s.filename} style={{
              display:"flex", alignItems:"center", gap:10,
              padding:"10px 16px", cursor:"pointer",
              background:isSelected ? "rgba(37,99,235,0.05)" : "transparent",
              borderLeft:isSelected ? `3px solid ${theme.blue}` : "3px solid transparent",
            }}>
              <input type="checkbox" checked={isSelected} onChange={() => toggleStrategy(s.filename)}
                style={{ width:16, height:16, accentColor:theme.blue, flexShrink:0 }}/>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12.5, fontWeight:isSelected?700:500, color:theme.text, fontFamily:fonts.mono, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.filename}</div>
                <div style={{ fontSize:10, color:statusColor, marginTop:2 }}>
                  {status} {s.instrument && `· ${s.instrument}`}
                </div>
              </div>
            </label>;
          })}
        </div>
        <div style={{ padding:"14px 18px", borderTop:`1px solid ${theme.divider}` }}>
          <div style={{ marginBottom:10 }}>
            <label style={{ fontSize:11, fontWeight:700, color:theme.text4, textTransform:"uppercase", letterSpacing:".5px", marginBottom:4, display:"block" }}>Account Size (Gesamt)</label>
            <input type="number" value={accountSize} onChange={e=>setAccountSize(Number(e.target.value))}
              style={{ width:"100%", padding:"8px 10px", borderRadius:6, border:`1px solid ${theme.border}`, fontSize:14, fontFamily:fonts.mono, fontWeight:700 }}/>
          </div>
          <Button primary onClick={runAnalysis} disabled={loading || selectedStrategies.length < 2} style={{ width:"100%", padding:"10px 16px" }}>
            {loading ? "Analysiere…" : "🔍 Portfolio analysieren"}
          </Button>
        </div>
      </Card>

      {/* ── Main Content: Analysis Results ─────────── */}
      <div>
        {!analysis && !loading && <Card style={{ padding:"60px 40px", textAlign:"center" }}>
          <div style={{ fontSize:48, marginBottom:14 }}>📊</div>
          <div style={{ fontSize:18, fontWeight:700, color:theme.text, marginBottom:8 }}>Portfolio-Analyse starten</div>
          <div style={{ fontSize:13, color:theme.text3, maxWidth:500, margin:"0 auto", lineHeight:1.6 }}>
            Wähle links 2 oder mehr Strategien aus die du parallel live schalten willst.
            Die Analyse zeigt dir: Korrelations-Matrix, kombinierter Drawdown, Margin-Bedarf, Concurrent Trades.
          </div>
          <div style={{ marginTop:20, padding:"14px 20px", borderRadius:10, background:theme.blueLight, textAlign:"left", fontSize:12, color:theme.text2, lineHeight:1.8 }}>
            <strong>Was du erfährst:</strong><br/>
            • Welche Strategien zu ähnlich sind (Korrelation &gt; 0.7 = redundant)<br/>
            • Wie oft mehrere Strategien gleichzeitig im Trade sind<br/>
            • Maximale Margin die dein Account jemals brauchte<br/>
            • Ob Drawdowns zeitgleich auftreten (Portfolio-Stress)<br/>
            • Diversifikations-Score
          </div>
        </Card>}

        {loading && <Card style={{ padding:"80px 40px", textAlign:"center" }}>
          <div style={{ fontSize:32, marginBottom:14 }}>⏳</div>
          <div style={{ fontSize:16, fontWeight:700, color:theme.text }}>Lade und analysiere Trades…</div>
        </Card>}

        {analysis && <AnalysisResults analysis={analysis} accountSize={accountSize} strategies={selectedStrategies}/>}
      </div>
    </div>
    </>}
  </div>;
}

/* ══════════════════════════════════════════════════
   ANALYSIS RESULTS COMPONENT
   ══════════════════════════════════════════════════ */
function AnalysisResults({ analysis, accountSize, strategies }) {
  const { strategies: sData, corrMatrix, combinedEquity, maxConcurrent, avgConcurrent, maxMargin, portfolioMaxDD, sumIndividualDDs, ddReduction, totalPortfolioPnl, concurrentSamples } = analysis;

  // Portfolio Health Score (0-100)
  const healthScore = (() => {
    let score = 100;
    // Penalize high correlations
    const avgCorr = corrMatrix.flat().filter((v,i) => i % corrMatrix.length !== Math.floor(i / corrMatrix.length))
      .reduce((s,v) => s + Math.abs(v), 0) / (corrMatrix.length * (corrMatrix.length - 1));
    score -= avgCorr * 30;
    // Penalize margin/account ratio
    const marginRatio = maxMargin / accountSize;
    if (marginRatio > 0.5) score -= (marginRatio - 0.5) * 60;
    // Penalize high portfolio DD
    if (portfolioMaxDD > 10) score -= (portfolioMaxDD - 10) * 2;
    return Math.max(0, Math.min(100, Math.round(score)));
  })();

  const healthColor = healthScore > 75 ? theme.greenText : healthScore > 50 ? theme.amberText : theme.redText;
  const healthLabel = healthScore > 75 ? "Ausgezeichnet" : healthScore > 50 ? "Akzeptabel" : "Problematisch";

  return <div>
    {/* Portfolio Health Summary */}
    <Card style={{ marginBottom:16, padding:"20px 24px", background:`linear-gradient(135deg, ${theme.greenLight}, ${theme.blueLight})` }}>
      <div style={{ display:"grid", gridTemplateColumns:"auto 1fr auto auto auto", gap:24, alignItems:"center" }}>
        <div style={{ textAlign:"center" }}>
          <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase", marginBottom:4 }}>Portfolio Score</div>
          <div style={{ fontSize:48, fontWeight:900, fontFamily:fonts.mono, color:healthColor, lineHeight:1 }}>{healthScore}</div>
          <div style={{ fontSize:11, fontWeight:700, color:healthColor, marginTop:4 }}>{healthLabel}</div>
        </div>
        <div>
          <div style={{ fontSize:14, fontWeight:800, color:theme.text, marginBottom:6 }}>
            {sData.length} Strategien · Combined P&L {formatPnl(totalPortfolioPnl)}
          </div>
          <div style={{ fontSize:12, color:theme.text2, lineHeight:1.6 }}>
            Combined Max DD: <strong>{portfolioMaxDD.toFixed(2)}%</strong> · Max gleichzeitig aktiv: <strong>{maxConcurrent}</strong> Trades · Peak Margin: <strong>{formatPnl(maxMargin).replace("+","")}</strong>
          </div>
        </div>
        <MiniStat label="DD-Reduktion" value={`${ddReduction.toFixed(1)}%`} color={theme.greenText} tooltip="vs. Summe der Einzel-DDs"/>
        <MiniStat label="Ø Concurrent" value={avgConcurrent.toFixed(1)} color={theme.blueText}/>
        <MiniStat label="Margin-Auslastung" value={`${((maxMargin/accountSize)*100).toFixed(1)}%`} color={maxMargin/accountSize > 0.5 ? theme.redText : theme.greenText}/>
      </div>
    </Card>

    {/* Individual Strategies */}
    <Card style={{ marginBottom:16 }}>
      <CardHeader title="Einzel-Performance" sub="Wie performen die Strategien einzeln?"/>
      <div style={{ padding:"4px 0" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead><tr style={{ borderBottom:`1px solid ${theme.divider}` }}>
            {["Strategie","P&L","Max DD","Win Rate","Trades","Anteil am Portfolio"].map(h =>
              <th key={h} style={{ padding:"8px 14px", fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase", textAlign:"left" }}>{h}</th>
            )}
          </tr></thead>
          <tbody>{sData.map((s, i) => <tr key={s.filename} style={{ borderBottom:`1px solid ${theme.divider}` }}>
            <td style={{ padding:"10px 14px", fontSize:12, fontFamily:fonts.mono, fontWeight:600 }}>{s.filename}</td>
            <td style={{ padding:"10px 14px", fontSize:12, fontWeight:700, fontFamily:fonts.mono, color:s.totalPnl > 0 ? theme.greenText : theme.redText }}>{formatPnl(s.totalPnl)}</td>
            <td style={{ padding:"10px 14px", fontSize:12, fontFamily:fonts.mono, color:theme.redText }}>{s.maxDD.toFixed(2)}%</td>
            <td style={{ padding:"10px 14px", fontSize:12, fontFamily:fonts.mono }}>{(s.winRate*100).toFixed(1)}%</td>
            <td style={{ padding:"10px 14px", fontSize:12, fontFamily:fonts.mono, color:theme.text3 }}>{s.trades.length}</td>
            <td style={{ padding:"10px 14px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ flex:1, height:6, background:theme.tint, borderRadius:3 }}>
                  <div style={{ height:"100%", borderRadius:3, background:theme.blue, width:`${Math.max(0, (s.totalPnl/totalPortfolioPnl)*100)}%` }}/>
                </div>
                <span style={{ fontSize:11, fontFamily:fonts.mono, fontWeight:700, color:theme.text2, minWidth:40, textAlign:"right" }}>{(Math.max(0, s.totalPnl/totalPortfolioPnl)*100).toFixed(0)}%</span>
              </div>
            </td>
          </tr>)}</tbody>
        </table>
      </div>
    </Card>

    {/* Correlation Matrix */}
    <Card style={{ marginBottom:16 }}>
      <CardHeader title="Korrelations-Matrix" sub="Pearson Correlation der täglichen P&L — Hohe Werte (>0.7) = redundante Strategien"/>
      <div style={{ padding:"14px 20px", overflowX:"auto" }}>
        <table style={{ borderCollapse:"collapse", fontFamily:fonts.mono }}>
          <thead><tr>
            <th style={{ padding:"6px 8px" }}></th>
            {sData.map(s => <th key={s.filename} style={{ padding:"8px 4px", fontSize:9, fontWeight:700, color:theme.text4, writingMode:"vertical-rl", transform:"rotate(180deg)", textAlign:"left", verticalAlign:"bottom", maxWidth:100 }}>
              {s.filename.replace(".yaml","").substring(0,20)}
            </th>)}
          </tr></thead>
          <tbody>{corrMatrix.map((row, i) => <tr key={i}>
            <td style={{ padding:"4px 10px", fontSize:11, fontWeight:700, color:theme.text2, textAlign:"right", whiteSpace:"nowrap" }}>
              {sData[i].filename.replace(".yaml","").substring(0,20)}
            </td>
            {row.map((v, j) => {
              const absV = Math.abs(v);
              const hue = v > 0 ? 0 : 210; // red for positive, blue for negative
              const alpha = absV;
              const color = i === j ? theme.tint : `hsla(${hue}, 70%, ${v > 0 ? 50 : 55}%, ${alpha * 0.7})`;
              const textColor = absV > 0.5 ? "#fff" : theme.text;
              return <td key={j} style={{
                padding:0, textAlign:"center", width:52, height:40,
                background:color,
                color:textColor, fontSize:11, fontWeight:700,
                border:`1px solid ${theme.divider}`,
              }} title={`${sData[i].filename} vs ${sData[j].filename}: ${v.toFixed(3)}`}>
                {i === j ? "—" : v.toFixed(2)}
              </td>;
            })}
          </tr>)}</tbody>
        </table>
        <div style={{ display:"flex", gap:16, marginTop:14, fontSize:11, color:theme.text3 }}>
          <span><span style={{ display:"inline-block", width:14, height:14, background:"hsla(0, 70%, 50%, 0.6)", verticalAlign:"middle", marginRight:5, borderRadius:2 }}/>Positive Korrelation (gleiche Richtung)</span>
          <span><span style={{ display:"inline-block", width:14, height:14, background:"hsla(210, 70%, 55%, 0.6)", verticalAlign:"middle", marginRight:5, borderRadius:2 }}/>Negative Korrelation (Diversifikation)</span>
        </div>
      </div>
    </Card>

    {/* Combined Equity Curve */}
    <Card style={{ marginBottom:16 }}>
      <CardHeader title="Kombinierte Equity-Kurve" sub={`Portfolio-Wachstum über alle ${sData.length} Strategien · Portfolio Max DD: ${portfolioMaxDD.toFixed(2)}%`}/>
      <div style={{ padding:"20px 24px" }}>
        <EquityChart data={combinedEquity}/>
      </div>
    </Card>

    {/* Concurrent Trades */}
    <Card style={{ marginBottom:16 }}>
      <CardHeader title="Parallele Trades" sub={`Max: ${maxConcurrent} · Durchschnitt: ${avgConcurrent.toFixed(1)} · Letzte 30 Tage`}/>
      <div style={{ padding:"20px 24px" }}>
        <ConcurrentChart data={concurrentSamples} max={maxConcurrent}/>
      </div>
    </Card>

    {/* Recommendations */}
    <Card style={{ marginBottom:16 }}>
      <CardHeader title="💡 Empfehlungen"/>
      <div style={{ padding:"14px 20px" }}>
        <Recommendations analysis={analysis} accountSize={accountSize}/>
      </div>
    </Card>
  </div>;
}

/* ── MiniStat ─────────────── */
function MiniStat({ label, value, color, tooltip }) {
  return <div style={{ textAlign:"center", minWidth:90 }} title={tooltip}>
    <div style={{ fontSize:10, fontWeight:700, color:theme.text4, textTransform:"uppercase", marginBottom:3 }}>{label}</div>
    <div style={{ fontSize:20, fontWeight:800, fontFamily:fonts.mono, color:color||theme.text }}>{value}</div>
  </div>;
}

/* ── Equity Chart (SVG) ─────────────── */
function EquityChart({ data }) {
  if (!data || data.length === 0) return null;
  const W = 900, H = 220, P = 40;
  const values = data.map(d => d.cumul);
  const minV = Math.min(...values, 0);
  const maxV = Math.max(...values, 1);
  const xStep = (W - 2*P) / (data.length - 1 || 1);
  const scaleY = v => H - P - ((v - minV) / (maxV - minV || 1)) * (H - 2*P);
  const points = data.map((d, i) => `${P + i*xStep},${scaleY(d.cumul)}`).join(" ");
  const areaPoints = `${P},${H-P} ${points} ${P + (data.length-1)*xStep},${H-P}`;

  return <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"auto" }}>
    <defs>
      <linearGradient id="eqg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={theme.green} stopOpacity="0.3"/>
        <stop offset="100%" stopColor={theme.green} stopOpacity="0"/>
      </linearGradient>
    </defs>
    {/* Zero line */}
    <line x1={P} y1={scaleY(0)} x2={W-P} y2={scaleY(0)} stroke={theme.divider} strokeDasharray="4 4"/>
    {/* Area */}
    <polygon points={areaPoints} fill="url(#eqg)"/>
    {/* Line */}
    <polyline points={points} fill="none" stroke={theme.green} strokeWidth="2"/>
    {/* Labels */}
    <text x={P-6} y={scaleY(maxV)+4} textAnchor="end" fontSize="10" fill={theme.text3}>{formatPnl(maxV)}</text>
    <text x={P-6} y={scaleY(0)+4} textAnchor="end" fontSize="10" fill={theme.text3}>$0</text>
    {data.length > 1 && <>
      <text x={P} y={H-15} fontSize="10" fill={theme.text3}>{data[0].date}</text>
      <text x={W-P} y={H-15} textAnchor="end" fontSize="10" fill={theme.text3}>{data[data.length-1].date}</text>
    </>}
  </svg>;
}

/* ── Concurrent Chart (SVG) ─────────────── */
function ConcurrentChart({ data, max }) {
  if (!data || data.length === 0) return null;
  const W = 900, H = 180, P = 40;
  const xStep = (W - 2*P) / (data.length - 1 || 1);
  const scaleY = v => H - P - (v / (max || 1)) * (H - 2*P);

  return <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"auto" }}>
    {data.map((d, i) => <rect key={i}
      x={P + i*xStep} y={scaleY(d.count)}
      width={Math.max(1, xStep - 0.3)}
      height={H - P - scaleY(d.count)}
      fill={d.count > 3 ? theme.amberText : d.count > 1 ? theme.blue : theme.greenBright||theme.green}
      opacity={0.8}
    />)}
    {/* Y-Axis */}
    {[0, Math.ceil(max/2), max].map(v => <g key={v}>
      <line x1={P} y1={scaleY(v)} x2={W-P} y2={scaleY(v)} stroke={theme.divider} strokeDasharray="2 3"/>
      <text x={P-6} y={scaleY(v)+4} textAnchor="end" fontSize="10" fill={theme.text3}>{v}</text>
    </g>)}
  </svg>;
}

/* ── Recommendations ─────────────── */
function Recommendations({ analysis, accountSize }) {
  const { corrMatrix, strategies, maxMargin, portfolioMaxDD, maxConcurrent } = analysis;
  const recs = [];

  // High correlations
  for (let i = 0; i < corrMatrix.length; i++) {
    for (let j = i+1; j < corrMatrix[i].length; j++) {
      if (Math.abs(corrMatrix[i][j]) > 0.7) {
        recs.push({
          type: "warning",
          text: <><strong>{strategies[i].filename}</strong> und <strong>{strategies[j].filename}</strong> haben Korrelation {corrMatrix[i][j].toFixed(2)}. Überlege ob du beide brauchst — diversifiziert wenig.</>
        });
      }
    }
  }

  // Margin pressure
  if (maxMargin / accountSize > 0.7) {
    recs.push({ type:"error", text:<>Peak Margin ({formatPnl(maxMargin).replace("+","")}) nutzt &gt;70% deines Accounts. <strong>Account-Größe erhöhen</strong> oder weniger Strategien parallel.</> });
  } else if (maxMargin / accountSize > 0.5) {
    recs.push({ type:"warning", text:<>Peak Margin nutzt {((maxMargin/accountSize)*100).toFixed(0)}% des Accounts. Keine Reserve für schlechte Phasen.</> });
  }

  // DD pressure
  if (portfolioMaxDD > 15) {
    recs.push({ type:"error", text:<>Portfolio Max DD von {portfolioMaxDD.toFixed(1)}% ist hoch. Für Prop Firms oft problematisch.</> });
  }

  // Concurrent overload
  if (maxConcurrent > 5) {
    recs.push({ type:"warning", text:<>Bis zu {maxConcurrent} parallele Trades. Prüfe ob dein Broker das Volumen effizient routet.</> });
  }

  // Diversification praise
  const lowCorrPairs = [];
  for (let i = 0; i < corrMatrix.length; i++) {
    for (let j = i+1; j < corrMatrix[i].length; j++) {
      if (Math.abs(corrMatrix[i][j]) < 0.3) lowCorrPairs.push([i,j]);
    }
  }
  if (lowCorrPairs.length > 0) {
    recs.push({ type:"success", text:<>{lowCorrPairs.length} Paare mit niedriger Korrelation (&lt;0.3) — gute Diversifikation.</> });
  }

  if (recs.length === 0) {
    recs.push({ type:"success", text:"Portfolio sieht ausgewogen aus. Keine kritischen Warnungen." });
  }

  return <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
    {recs.map((r, i) => {
      const bg = r.type === "error" ? theme.redLight : r.type === "warning" ? (theme.amberLight||"rgba(251,191,36,0.08)") : theme.greenLight;
      const border = r.type === "error" ? theme.redText : r.type === "warning" ? theme.amberText : theme.greenText;
      const icon = r.type === "error" ? "🚨" : r.type === "warning" ? "⚠️" : "✓";
      return <div key={i} style={{ padding:"10px 14px", borderRadius:8, background:bg, borderLeft:`3px solid ${border}`, fontSize:12.5, color:theme.text2, lineHeight:1.6 }}>
        <strong>{icon}</strong>&nbsp;&nbsp;{r.text}
      </div>;
    })}
  </div>;
}
